import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Play, Wrench, ArrowRight, Languages, X, AlertTriangle, Globe, Trophy, Share2, Copy, Check, MessageCircle, CalendarDays, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { trackAppEvent } from "@/lib/pmf";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { RoadmapGraph } from "@/components/RoadmapGraph";
import { Button } from "@/components/ui/button";
import { getIcon } from "@/lib/icons";
import { toast } from "sonner";

const TILE = {
  lime: "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400",
  pink: "bg-pink-50 text-pink-600 dark:bg-pink-500/10 dark:text-pink-400",
  cyber: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400",
  tangerine: "bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400",
};
const tile = (c) => TILE[c] || TILE.cyber;

function LanguagePicker({ languages, current, onPick, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] grid place-items-center p-4 bg-foreground/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="language-picker"
    >
      <motion.div
        initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card border border-border shadow-xl rounded-2xl p-6"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-xl flex items-center gap-2">
            <Languages className="h-5 w-5 text-primary" /> Pick a language
          </h3>
          <button data-testid="language-picker-close" onClick={onClose} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-accent transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mt-1">We'll rebuild your path with lessons genuinely in that language.</p>
        <div className="mt-4 grid gap-2 max-h-[50vh] overflow-y-auto">
          {languages.map((l) => (
            <button
              key={l.code}
              data-testid={`language-option-${l.code}`}
              disabled={l.code === current}
              onClick={() => onPick(l.code)}
              className={`flex items-center justify-between rounded-xl border border-border p-3 text-left transition-colors ${
                l.code === current ? "bg-secondary opacity-60 cursor-not-allowed" : "bg-background hover:bg-accent"
              }`}
            >
              <span className="font-heading font-semibold">{l.label}</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">{l.topics_covered} topics</span>
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

function ShareModal({ url, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };
  const text = encodeURIComponent("Check out my personalized AI learning roadmap on AI Pathshaalaa 🚀");
  const enc = encodeURIComponent(url);
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] grid place-items-center p-4 bg-foreground/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="share-modal"
    >
      <motion.div
        initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card border border-border shadow-xl rounded-2xl p-6"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-xl flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" /> Share my roadmap</h3>
          <button data-testid="share-modal-close" onClick={onClose} className="grid place-items-center h-8 w-8 rounded-lg hover:bg-accent transition-colors"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-sm text-muted-foreground mt-1">Anyone with this link can view your AI path and build their own — a great nudge for friends.</p>
        <div className="mt-4 flex gap-2">
          <input data-testid="share-url" readOnly value={url} onFocus={(e) => e.target.select()}
            className="flex-1 min-w-0 rounded-lg border border-border bg-background px-3 text-sm font-medium" />
          <Button data-testid="share-copy" onClick={copy} className="bg-primary text-primary-foreground shrink-0">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <a href={`https://wa.me/?text=${text}%20${enc}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-semibold hover:bg-accent transition-colors">
            <MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp
          </a>
          <a href={`https://twitter.com/intent/tweet?text=${text}&url=${enc}`} target="_blank" rel="noopener noreferrer" data-testid="share-twitter"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-semibold hover:bg-accent transition-colors">
            <Share2 className="h-4 w-4" /> Share on X
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const openParam = new URLSearchParams(location.search).get("open");
  const { user } = useAuth();
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLang, setShowLang] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [toolCourses, setToolCourses] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);

  const pollRef = useRef(0);
  const roadmapTracked = useRef(false);
  const load = async () => {
    try {
      const { data } = await api.get("/path");
      setPath(data);
      if (!roadmapTracked.current) {
        roadmapTracked.current = true;
        trackAppEvent("viewed_roadmap");
      }
      const hasLocked = data.items?.some((it) => it.locked);
      if (hasLocked && pollRef.current < 6) {
        pollRef.current += 1;
        setTimeout(load, 9000);
      }
    } catch (e) {
      if (e.response?.status === 404) navigate("/quiz", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/tools/my").then(({ data }) => setToolCourses(data.courses || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openLesson = (videoId) => { if (videoId) navigate(`/lesson/${videoId}`); };

  const switchLanguage = async (code) => {
    setSwitching(true);
    try {
      await api.post("/switch-language", { language: code });
      setShowLang(false);
      pollRef.current = 0;
      setLoading(true);
      await load();
      toast.success("Path rebuilt in your new language!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSwitching(false);
    }
  };

  const CREATOR_MSG = {
    indian: "Now showing Indian creators only 🇮🇳",
    global: "Now showing global (international) creators 🌍",
    both: "Now showing a mix of Indian + global creators ✨",
  };

  const setCreator = async (value) => {
    setSwitching(true);
    try {
      await api.post("/switch-creator", { creator_pref: value });
      pollRef.current = 0;
      setLoading(true);
      await load();
      toast.success(CREATOR_MSG[value] || "Creators updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSwitching(false);
    }
  };

  const cycleCreator = () => {
    const order = ["both", "indian", "global"];
    const next = order[(order.indexOf(path.creator_pref) + 1) % order.length];
    setCreator(next);
  };

  const shareRoadmap = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/share/roadmap");
      setShareUrl(`${window.location.origin}/r/${data.token}`);
      setShowShare(true);
      trackAppEvent("shared_roadmap");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create a share link.");
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!path) return null;

  const firstName = (user?.name || "there").split(" ")[0];
  const lockedCount = (path.items || []).filter((it) => it.locked).length;
  const partial = path.language_available && lockedCount > 0 && path.language !== "english";
  const nextItem = (path.items || []).find((it) => it.video_id === path.next_video_id);
  const nextVideo = nextItem?.videos?.find((v) => v.video_id === path.next_video_id);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AnimatePresence>
        {showLang && (
          <LanguagePicker
            languages={path.available_languages || []}
            current={path.language}
            onPick={switchLanguage}
            onClose={() => setShowLang(false)}
          />
        )}
        {showShare && <ShareModal url={shareUrl} onClose={() => setShowShare(false)} />}
      </AnimatePresence>

      {/* desktop sidebar */}
      <aside className="hidden lg:flex fixed top-16 left-0 bottom-0 w-72 flex-col overflow-y-auto border-r border-border bg-card/40 backdrop-blur-md p-7 z-30">
        <DashboardSidebar user={user} path={path} onShowLang={() => setShowLang(true)} onCycleCreator={cycleCreator} switching={switching} />
      </aside>

      <main className="lg:pl-72 pt-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-5xl mx-auto px-5 md:px-8 lg:px-12 py-8 md:py-10 flex flex-col gap-10"
          data-testid="dashboard-header"
        >
          {/* continue learning hero — the ONE primary action */}
          <section data-testid="continue-hero" className="relative overflow-hidden rounded-3xl border border-primary/25 bg-card p-6 md:p-8 beam-top">
            <span className="orb h-64 w-64 bg-primary/40 -top-20 -right-16" />
            <p className="relative text-sm font-medium text-muted-foreground">Welcome back, {firstName} 👋</p>
            {path.finished ? (
              <div className="relative mt-4 flex flex-col sm:flex-row sm:items-center gap-5">
                <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2.5 rounded-full font-semibold text-sm w-fit">
                  <Trophy className="h-4 w-4" /> Path complete — absolute legend! 🏆
                </span>
                <div className="flex items-center gap-4 flex-wrap">
                  <button data-testid="share-roadmap-btn" onClick={shareRoadmap} disabled={sharing}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
                    {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share my roadmap
                  </button>
                  <button data-testid="retake-quiz" onClick={() => navigate("/quiz")}
                    className="text-sm font-semibold text-muted-foreground hover:text-foreground underline-offset-4 hover:underline">
                    Retake quiz
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative mt-4 flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                {nextVideo?.thumbnail && (
                  <img src={nextVideo.thumbnail} alt="" data-testid="continue-hero-thumb"
                    className="w-full sm:w-56 aspect-video object-cover rounded-2xl border border-border shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                    Up next{nextItem ? ` · ${nextItem.topic_title}` : ""}
                  </p>
                  <h2 data-testid="continue-hero-title" className="mt-1.5 font-heading font-bold text-2xl md:text-3xl leading-tight line-clamp-2">
                    {nextVideo?.video_title || nextItem?.topic_title || "Your next lesson"}
                  </h2>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-2 flex-1 max-w-[220px] rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${path.percent}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground">{path.completed_count}/{path.total} · {path.percent}%</span>
                  </div>
                  <div className="mt-5 flex items-center gap-4 flex-wrap">
                    <Button size="lg" data-testid="continue-learning" onClick={() => openLesson(path.next_video_id)} disabled={!path.next_video_id}
                      className="rounded-full px-7">
                      <Play className="h-4 w-4 fill-current" /> {path.completed_count > 0 ? "Continue learning" : "Start your first lesson"}
                    </Button>
                    <button data-testid="share-roadmap-btn" onClick={shareRoadmap} disabled={sharing}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
                      {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share
                    </button>
                    <button data-testid="retake-quiz" onClick={() => navigate("/quiz")}
                      className="text-sm font-semibold text-muted-foreground hover:text-foreground underline-offset-4 hover:underline">
                      Retake quiz
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* slim tools strip */}
          <button data-testid="mode-tools-btn" onClick={() => navigate("/tools")}
            className="group flex items-center gap-4 rounded-2xl border border-border bg-card px-5 py-4 text-left hover:border-primary/40 transition-colors">
            <span className="grid place-items-center h-10 w-10 rounded-xl bg-pink-50 text-pink-600 shrink-0"><Wrench className="h-5 w-5" /></span>
            <span className="flex-1 min-w-0">
              <span className="block font-heading font-bold">Learn a specific tool</span>
              <span className="block text-xs text-muted-foreground truncate">Complete courses on ChatGPT, Gemini, Claude + 36 more</span>
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
          </button>

          {/* mobile profile card */}
          <div className="lg:hidden rounded-2xl border border-border bg-card p-5">
            <DashboardSidebar user={user} path={path} onShowLang={() => setShowLang(true)} onCycleCreator={cycleCreator} switching={switching} />
          </div>

          {/* why this path */}

          {/* your tool courses — the rest of the AI universe */}
          {toolCourses.length > 0 && (
            <div data-testid="your-tool-courses">
              <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-heading font-bold text-2xl text-foreground">Your tool courses</h2>
                  <p className="text-sm text-muted-foreground mt-1">Complete, structured courses on the AI tools you picked — pick up where you left off.</p>
                </div>
                <Button variant="outline" size="sm" data-testid="browse-more-tools" onClick={() => navigate("/tools")}>
                  <Wrench className="h-4 w-4" /> Browse more tools
                </Button>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {toolCourses.map((c) => {
                  const Icon = getIcon(c.icon);
                  const denom = c.total || c.lesson_count || 0;
                  return (
                    <div key={c.tool_slug} data-testid={`tool-course-card-${c.tool_slug}`}
                      className="rounded-2xl border border-border bg-card p-5 flex flex-col shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-3">
                        <span className={`grid place-items-center h-10 w-10 shrink-0 rounded-xl ${tile(c.color)}`}><Icon className="h-5 w-5" /></span>
                        <div className="min-w-0">
                          <h3 className="font-heading font-bold text-base truncate">{c.tool_name}</h3>
                          <p className="text-xs text-muted-foreground">{c.completed_count}/{denom} lessons · {c.language_label}</p>
                        </div>
                      </div>
                      <div className="mt-4 h-2 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${c.percent}%` }} />
                      </div>
                      <div className="mt-4 flex items-center gap-4">
                        <button data-testid={`tool-course-continue-${c.tool_slug}`}
                          onClick={() => (c.next_video_id ? openLesson(c.next_video_id) : navigate(`/tools/${c.tool_slug}`))}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline underline-offset-4">
                          <Play className="h-3.5 w-3.5 fill-current" /> {c.completed_count > 0 ? "Continue" : "Start"}
                        </button>
                        <button onClick={() => navigate(`/tools/${c.tool_slug}`)}
                          className="text-sm font-medium text-muted-foreground hover:text-foreground">View course</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* roadmap / language states */}
          {!path.language_available ? (
            <div className="rounded-2xl border border-border bg-amber-50 dark:bg-amber-500/10 p-6 md:p-8" data-testid="language-unavailable">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-6 w-6" />
                <h2 className="font-heading font-bold text-2xl text-foreground">No lessons in {path.language_label} yet</h2>
              </div>
              <p className="mt-2 text-muted-foreground max-w-xl">
                {path.creator_pref !== "both"
                  ? `You're set to ${path.creator_label.toLowerCase()} — we haven't curated enough ${path.creator_label.toLowerCase()} lessons for these topics yet. Switch to a mix of all creators to unlock them now, or try another language.`
                  : `We couldn't find genuinely-${path.language_label} videos that meet our quality bar for these topics yet. Pick another language and we'll instantly rebuild your path with great lessons.`}
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button data-testid="pick-language-btn" onClick={() => setShowLang(true)} disabled={switching} className="bg-primary text-primary-foreground">
                  {switching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />} Choose another language <ArrowRight className="h-4 w-4" />
                </Button>
                {path.creator_pref !== "both" && (
                  <Button data-testid="show-all-creators-btn" variant="outline" onClick={() => setCreator("both")} disabled={switching}>
                    <Globe className="h-4 w-4" /> Show all creators
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              {partial && (
                <div className="rounded-2xl border border-border bg-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="partial-language-banner">
                  <p className="text-sm text-muted-foreground flex-1">
                    Some lessons aren't available in <b className="text-foreground">{path.language_label}</b> yet — they'll unlock as we curate them, or switch language for full coverage.
                  </p>
                  <Button size="sm" variant="outline" data-testid="partial-change-language" onClick={() => setShowLang(true)}>
                    <Languages className="h-4 w-4" /> Change
                  </Button>
                </div>
              )}

              <div>
                <h2 className="font-heading font-bold text-2xl text-foreground">Your learning roadmap</h2>
                {path.total_videos > 0 && (
                  <p className="text-sm text-muted-foreground mt-1" data-testid="roadmap-depth">
                    {path.total_videos} lessons · {path.total} modules — multiple teachers per topic, so it actually sticks.
                  </p>
                )}

                {path.weeks_total > 0 && (
                  <div data-testid="pacing-card" className="mt-5 mb-6 rounded-2xl border border-border bg-card p-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <CalendarDays className="h-5 w-5 text-primary" />
                      <h3 className="font-heading font-bold text-lg">Your weekly plan</h3>
                      <span data-testid="pacing-week-badge" className="ml-auto text-xs font-bold rounded-full bg-primary/10 text-primary px-3 py-1">
                        Week {path.current_week} of {path.weeks_total}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5" data-testid="pacing-summary">
                      Paced for your {path.time_per_week_label || "schedule"} — finish the whole path in about{" "}
                      <b className="text-foreground">{path.weeks_total} {path.weeks_total === 1 ? "week" : "weeks"}</b>. No cramming needed.
                    </p>
                    {(() => {
                      const thisWeek = (path.items || []).filter((it) => it.week === path.current_week && !it.completed && !it.locked).slice(0, 4);
                      if (thisWeek.length === 0) return null;
                      return (
                        <div className="mt-4 space-y-2" data-testid="pacing-week-items">
                          {thisWeek.map((it) => (
                            <button key={it.topic_slug} data-testid={`pacing-item-${it.topic_slug}`}
                              onClick={() => openLesson(it.video_id)}
                              className="w-full flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3 text-left hover:border-primary/40 transition-colors">
                              <Play className="h-4 w-4 text-primary shrink-0 fill-current" />
                              <span className="font-medium text-sm truncate flex-1">{it.topic_title}</span>
                              <span className="text-xs text-muted-foreground shrink-0 inline-flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" /> ~{it.est_minutes} min
                              </span>
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
                <RoadmapGraph
                  levels={path.levels}
                  nextVideoId={path.next_video_id}
                  languageLabel={path.language_label}
                  watchedVideoIds={path.watched_video_ids}
                  onSelect={openLesson}
                  forceOpenSlug={openParam}
                />
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
